import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { RootState } from "../ngrx/root.model";
import { finalize, take } from "rxjs";
import { LoaderService } from "./loader.service";
import { VigilantVersionService } from "../api/vigilant/version/version.service";
import { AppActions } from "../ngrx/app/app.reducer";
import { VigilantUsersService } from "../api/vigilant/users/users.service";
import { UsersActions } from "../api/vigilant/users/users.reducer";
import { VigilantOrgsService } from "../api/vigilant/orgs/orgs.service";
import { OrgsActions } from "../api/vigilant/orgs/orgs.reducer";
import { VigilantRealmsService } from "../api/vigilant/realms/realms.service";
import { RealmsActions } from "../api/vigilant/realms/realms.reducer";

export type DataServiceModels = "version" | "self" | "users" | "orgs" | "realms";

@Injectable({ providedIn: "root" })
export class DataService {
  constructor(
    private store: Store<RootState>,
    private loader: LoaderService,
    private vigilantVersionService: VigilantVersionService,
    private vigilantUsersService: VigilantUsersService,
    private vigilantOrgsService: VigilantOrgsService,            
    private vigilantRealmsService: VigilantRealmsService
  ) { }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initializeData(models: DataServiceModels[], forceRefresh = false, callback?: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const methods: { call: any }[] = [];

    if (models.includes("version")) {
      methods.push({ call: (): void => this.getVersion(forceRefresh) });
    }
    if (models.includes("self")) {
      methods.push({ call: (): void => this.getCurrentUser() });
    }
    if (models.includes("users")) {
      methods.push({ call: (): void => this.getAllActiveUsers(forceRefresh) });
    }
    if (models.includes("orgs")) {
      methods.push({ call: (): void => this.getAllActiveOrgs(forceRefresh) });
    }   
    if (models.includes("realms")) {
      methods.push({ call: (): void => this.getAllActiveRealms(forceRefresh) });
    }

    this.store.dispatch(AppActions.ShowBlockUI({ spinner: true }));

    // Data loading is throttled upon forced user action to enforce minimum spinner visibility uiux and debounce multiple calls
    setTimeout(
      () => {
        this.loader
          .load(methods)
          .pipe(
            finalize(() => {
              this.store.dispatch(AppActions.HideBlockUI({ spinner: false }));

              if (callback) {
                callback();
              }
            })
          )
          .subscribe();
      },
      forceRefresh ? 200 : 0
    );
  }

  getAllActiveUsers(forceRefresh = false): void {
    const token = "getAllActiveUsers";

    this.store.pipe(take(1)).subscribe((state: RootState) => {
      if (!forceRefresh && state.users.users) {
        this.loader.complete({ call: token });
        return;
      }

      this.vigilantUsersService.getAllActiveUsers().subscribe({
        next: (response) => {
          this.store.dispatch(UsersActions.SetUsers({ users: response }));
          this.loader.complete({ call: token });
        },
        error: () => {
          this.loader.complete({ call: token }, true);
        },
      });
    });
  }

  getAllActiveOrgs(forceRefresh = false): void {
    const token = "getAllActiveOrgs";

    this.store.pipe(take(1)).subscribe((state: RootState) => {
      if (!forceRefresh && state.orgs.orgs) {
        this.loader.complete({ call: token });
        return;
      }

      this.vigilantOrgsService.getAllActiveOrgs().subscribe({
        next: (response) => {
          this.store.dispatch(OrgsActions.SetOrgs({ orgs: response }));
          this.loader.complete({ call: token });
        },
        error: () => {
          this.loader.complete({ call: token }, true);
        },
      });
    });
  }

  getAllActiveRealms(forceRefresh = false): void {
    const token = "getAllActiveRealms";

    this.store.pipe(take(1)).subscribe((state: RootState) => {
      if (!forceRefresh && state.realms.realms) {
        this.loader.complete({ call: token });
        return;
      }

      this.vigilantRealmsService.getAllActiveRealms().subscribe({
        next: (response) => {
          this.store.dispatch(RealmsActions.SetRealms({ realms: response }));
          this.loader.complete({ call: token });
        },
        error: () => {
          this.loader.complete({ call: token }, true);
        },
      });
    });
  }
 
  getVersion(forceRefresh = false): void {
    const token = "getVersion";

    this.store.pipe(take(1)).subscribe((state: RootState) => {
      if (!forceRefresh && state.app.apiVersion) {
        this.loader.complete({ call: token });
        return;
      }

      this.vigilantVersionService.getVersion().subscribe({
        next: (response) => {
          this.store.dispatch(AppActions.SetAPIVersion({ apiVersion: response }));
          this.loader.complete({ call: token });
        },
        error: () => {
          this.loader.complete({ call: token }, true);
        },
      });
    });
  }

  getCurrentUser(): void {
    const token = "getCurrentUser";

    this.vigilantUsersService.getCurrentUser().subscribe({
      next: (response) => {
        this.store.dispatch(
          AppActions.SetUserInfo({
            userSuperAdmin: response.role === "VigilantAdmin",
            userId: response.id,
            userEmail: response.email,
            userFirstName: response.firstName,
            userLastName: response.lastName,
            userRootOrgId: response.rootOrgId,
            userSelf: response,
          })
        );
        this.loader.complete({ call: token });
      },
      error: () => {
        this.loader.complete({ call: token }, true);
      },
    });
  }
}
